import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { promptService } from '../services/promptService';
import type { Prompt } from '../types';
import { FORGE_ARCHETYPES } from '../constants/ForgeArchetypesData';
import {
  SearchIcon,
  SparklesIcon,
  SlidersIcon,
  PlusIcon,
  SunIcon,
  MoonIcon,
  HomeIcon,
  UserIcon,
  CameraIcon,
  XIcon,
} from './icons/Icons';

interface PaletteItem {
  id: string;
  category: 'Actions' | 'TheForge Archetypes' | 'Prompts';
  title: string;
  subtitle?: string;
  badge?: string;
  icon?: React.ReactNode;
  imageUrl?: string;
  action: () => void;
}

export const OPEN_COMMAND_PALETTE_EVENT = 'promptera:open-command-palette';

export const openCommandPalette = () => {
  window.dispatchEvent(new CustomEvent(OPEN_COMMAND_PALETTE_EVENT));
};

export const CommandPalette: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prompts, setPrompts] = useState<Prompt[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Load public prompts for quick searching
  useEffect(() => {
    const unsubscribe = promptService.listenToPublicPrompts(
      (data) => setPrompts(data || []),
      (err) => console.warn('Could not pre-fetch prompts for command palette:', err)
    );
    return () => unsubscribe();
  }, []);

  // Keyboard shortcut listener: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    const handleCustomOpen = () => {
      setIsOpen(true);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, handleCustomOpen);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, handleCustomOpen);
    };
  }, []);

  // Autofocus input on open and lock background scroll
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      document.body.style.overflow = 'hidden';
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  // Construct search items
  const allItems = useMemo<PaletteItem[]>(() => {
    const items: PaletteItem[] = [];

    // 1. Quick Actions
    items.push({
      id: 'action-forge',
      category: 'Actions',
      title: 'Open TheForge™ Visual Engine',
      subtitle: 'Interactive 3-step prompt generator & recipe builder',
      badge: 'Visual Studio',
      icon: <SlidersIcon className="h-4 w-4 text-accent" />,
      action: () => {
        navigate('/forge');
        setIsOpen(false);
      },
    });

    items.push({
      id: 'action-new',
      category: 'Actions',
      title: 'Create New Prompt',
      subtitle: 'Write a formula from scratch, extract, or reverse engineer',
      badge: 'Create',
      icon: <PlusIcon className="h-4 w-4 text-emerald-500" />,
      action: () => {
        navigate('/prompts/new');
        setIsOpen(false);
      },
    });

    items.push({
      id: 'action-extract',
      category: 'Actions',
      title: 'Extract Prompt from Screenshot (OCR)',
      subtitle: 'Clean prompt text from Instagram, Pinterest, X, or Discord',
      badge: 'Vision AI',
      icon: <CameraIcon className="h-4 w-4 text-cyan-500" />,
      action: () => {
        navigate('/prompts/new?mode=extract');
        setIsOpen(false);
      },
    });

    items.push({
      id: 'action-theme',
      category: 'Actions',
      title: theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode',
      subtitle: 'Toggle between clean light and dark studio themes',
      badge: 'Appearance',
      icon: theme === 'dark' ? <SunIcon className="h-4 w-4 text-amber-400" /> : <MoonIcon className="h-4 w-4 text-indigo-400" />,
      action: () => {
        toggleTheme();
        setIsOpen(false);
      },
    });

    items.push({
      id: 'action-browse',
      category: 'Actions',
      title: 'Browse Public Prompt Catalog',
      subtitle: 'Explore community prompts, filters, and curated formulas',
      badge: 'Explore',
      icon: <SearchIcon className="h-4 w-4 text-muted-foreground" />,
      action: () => {
        navigate('/browse');
        setIsOpen(false);
      },
    });

    if (user) {
      items.push({
        id: 'action-dashboard',
        category: 'Actions',
        title: 'My Dashboard & Library',
        subtitle: 'View saved prompts, custom collections, and analytics',
        badge: 'Library',
        icon: <HomeIcon className="h-4 w-4 text-accent" />,
        action: () => {
          navigate('/dashboard');
          setIsOpen(false);
        },
      });

      items.push({
        id: 'action-profile',
        category: 'Actions',
        title: 'View / Edit My Profile',
        subtitle: `Logged in as ${user.displayName || user.email}`,
        badge: 'Account',
        icon: <UserIcon className="h-4 w-4 text-muted-foreground" />,
        action: () => {
          navigate(`/profile/${user.uid}`);
          setIsOpen(false);
        },
      });
    }

    // 2. TheForge Archetypes
    FORGE_ARCHETYPES.forEach((archetype) => {
      items.push({
        id: `archetype-${archetype.id}`,
        category: 'TheForge Archetypes',
        title: archetype.name,
        subtitle: archetype.subtitle,
        badge: archetype.badge,
        imageUrl: archetype.sampleImageUrl,
        action: () => {
          navigate(`/forge?archetype=${archetype.id}`);
          setIsOpen(false);
        },
      });
    });

    // 3. Prompts
    prompts.forEach((p) => {
      const displayImg = p.thumbnailUrl || (p.exampleMedia && p.exampleMedia[0]?.fileUrl);
      items.push({
        id: `prompt-${p.id}`,
        category: 'Prompts',
        title: p.title,
        subtitle: p.shortDescription || p.fullPrompt.slice(0, 80) + '...',
        badge: p.category,
        imageUrl: displayImg,
        icon: <SparklesIcon className="h-4 w-4 text-accent" />,
        action: () => {
          navigate(`/prompts/${p.id}`);
          setIsOpen(false);
        },
      });
    });

    return items;
  }, [prompts, theme, user, navigate, toggleTheme]);

  // Filter items based on user search query
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Return top actions and top archetypes
      return allItems.filter(
        (item) => item.category === 'Actions' || item.category === 'TheForge Archetypes'
      );
    }

    return allItems.filter((item) => {
      const matchTitle = item.title.toLowerCase().includes(q);
      const matchSubtitle = item.subtitle?.toLowerCase().includes(q) || false;
      const matchBadge = item.badge?.toLowerCase().includes(q) || false;
      return matchTitle || matchSubtitle || matchBadge;
    });
  }, [allItems, query]);

  // Keep selected index within bounds
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll active item into view
  useEffect(() => {
    if (listRef.current) {
      const activeEl = listRef.current.querySelector('[data-active="true"]');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Handle arrow navigation & selection inside input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, filteredItems.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % Math.max(1, filteredItems.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredItems[selectedIndex]) {
        filteredItems[selectedIndex].action();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  if (!isOpen) return null;

  // Group filtered items by category
  const categories = Array.from(new Set(filteredItems.map((item) => item.category)));

  let globalCounter = 0;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center p-3 sm:p-4 pt-14 sm:pt-20">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-200 animate-fade-in"
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      {/* Palette Container */}
      <div className="relative z-50 w-full max-w-2xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl backdrop-blur-xl animate-scale-in flex flex-col">
        {/* Search Header Bar */}
        <div className="relative flex items-center border-b border-border px-4 py-3.5 bg-card/80 backdrop-blur-md">
          <SearchIcon className="h-5 w-5 text-muted-foreground mr-3 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search prompts, archetypes, or quick actions..."
            className="w-full bg-transparent text-sm sm:text-base font-sans text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors mr-2 shrink-0"
              title="Clear search"
            >
              <XIcon className="h-4 w-4" />
            </button>
          ) : null}
          <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-border bg-muted/70 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground font-semibold">
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto overscroll-contain p-2 space-y-3">
          {filteredItems.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground space-y-2">
              <SearchIcon className="h-8 w-8 mx-auto opacity-30" />
              <p className="text-sm font-medium">No results found for "{query}"</p>
              <p className="text-xs text-muted-foreground/70">
                Try searching for "selfie", "cinematic", "create", or "theme".
              </p>
            </div>
          ) : (
            categories.map((category) => {
              const categoryItems = filteredItems.filter((i) => i.category === category);
              return (
                <div key={category} className="space-y-1">
                  <div className="px-3 py-1 text-[10px] font-mono font-bold uppercase tracking-wider text-muted-foreground/70">
                    {category}
                  </div>
                  <div className="space-y-0.5">
                    {categoryItems.map((item) => {
                      const itemIndex = globalCounter++;
                      const isSelected = itemIndex === selectedIndex;

                      return (
                        <div
                          key={item.id}
                          data-active={isSelected ? 'true' : 'false'}
                          onClick={item.action}
                          onMouseEnter={() => setSelectedIndex(itemIndex)}
                          className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all text-left ${
                            isSelected
                              ? 'bg-accent/15 text-accent dark:bg-accent/20 dark:text-accent-foreground ring-1 ring-accent/30 font-semibold shadow-sm'
                              : 'hover:bg-muted text-foreground/90'
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {item.imageUrl ? (
                              <img
                                src={item.imageUrl}
                                alt={item.title}
                                className="h-8 w-8 rounded-lg object-cover border border-border shrink-0"
                                loading="lazy"
                              />
                            ) : (
                              <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/60 bg-muted/60 shrink-0">
                                {item.icon}
                              </div>
                            )}

                            <div className="min-w-0">
                              <div className="text-sm font-semibold truncate text-foreground leading-tight">
                                {item.title}
                              </div>
                              {item.subtitle && (
                                <div className="text-xs text-muted-foreground truncate leading-tight mt-0.5">
                                  {item.subtitle}
                                </div>
                              )}
                            </div>
                          </div>

                          {item.badge && (
                            <span className="shrink-0 text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full border border-border/80 bg-muted/60 text-muted-foreground">
                              {item.badge}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer Navigation Hints */}
        <div className="flex items-center justify-between border-t border-border px-4 py-2 text-[11px] text-muted-foreground bg-muted/30 font-mono">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border bg-card px-1 py-0.5 text-[9px]">↑</kbd>
              <kbd className="rounded border border-border bg-card px-1 py-0.5 text-[9px]">↓</kbd> to navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border bg-card px-1.5 py-0.5 text-[9px]">↵</kbd> to select
            </span>
          </div>
          <span className="hidden sm:inline">Spotlight Command Palette</span>
        </div>
      </div>
    </div>,
    document.body
  );
};
